import Dexie, { Table } from "dexie";
import type { AiProfile } from "@/types/ai";
import type { Profile } from "@/types/Profile";
import type { VaultFile, VaultChunk } from "@/types/vault";
import type { ToolRegistryEntry } from "@/types/tools";
import type { Project } from "@/types/project";
import type { Tile } from "@/types/tile";
import type { ShareLink, EmbedConfig } from "@/types/sharing";
import type { AnalyticsEvent, ProfileAnalytics } from "@/types/analytics";
import type { ClarityMetrics } from "@/lib/analytics/clarityTracking";
import type { Team, TeamMember, Comment } from "@/types/collaboration";

export interface Channel {
  id: string;
  teamId: string;
  name: string;
  description?: string;
  isPrivate: boolean;
  createdAt: number;
}

export interface TeamMessage {
  id: string;
  teamId: string;
  senderId: string;
  senderType: "human" | "ai";
  senderName: string;
  senderRole?: string;
  channelId: string;
  content: string;
  mentions?: string[];
  createdAt: number;
  isAIResponse?: boolean;
}
import type { MarketplaceListing, Purchase, SubscriptionAddOn } from "@/types/monetization";
import type { Integration } from "@/types/integration";
import type { KarmaProfile } from "@/types/karmaProfile";
import type { Account } from "@/types/account";

export interface AssistantMemoryEntry {
  id: string;
  userId: string;
  aiId: string;
  channelId?: string;
  key: string;
  value: string;
  scope?: "user" | "conversation" | "global";
  threadId?: string;
  createdAt: number;
  updatedAt: number;
  sourceMessageId?: string;
}

export interface Conversation {
  id: string;
  aiId: string; // AI entity ID
  userId: string; // Owner ID
  name?: string; // Optional conversation name
  createdAt: number;
  updatedAt: number;
  lastMessageAt?: number;
  messageCount: number;
  archived: boolean;
  metadata?: Record<string, any>;
}

export interface ChatMessage {
  id: string;
  conversationId?: string; // Links to Conversation (optional for backward compatibility)
  profileId: string; // Legacy: keep for backward compatibility
  projectId?: string; // Scoped to project if exists
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: number;
  images?: Array<{ url: string; prompt?: string }>; // Generated images
  videos?: Array<{ url?: string; prompt?: string; operationName?: string; status?: string }>; // Generated videos
}

export interface ProfileVersion {
  id: string;
  profileId: string;
  version: number;
  data: AiProfile; // Snapshot of profile at this version
  createdAt: number;
  createdBy: string;
  note?: string; // Version description
}

export class YourDreamAIDatabase extends Dexie {
  profiles!: Table<Profile>;
  aiProfiles!: Table<AiProfile>;
  conversations!: Table<Conversation>;
  vaultFiles!: Table<VaultFile>;
  vaultChunks!: Table<VaultChunk>;
  chatMessages!: Table<ChatMessage>;
  toolRegistry!: Table<ToolRegistryEntry>;
  projects!: Table<Project>;
  tiles!: Table<Tile>;
  shareLinks!: Table<ShareLink>;
  embedConfigs!: Table<EmbedConfig>;
  analyticsEvents!: Table<AnalyticsEvent>;
  profileAnalytics!: Table<ProfileAnalytics>;
  teams!: Table<Team>;
  teamMembers!: Table<TeamMember>;
  comments!: Table<Comment>;
  marketplaceListings!: Table<MarketplaceListing>;
  purchases!: Table<Purchase>;
  subscriptionAddOns!: Table<SubscriptionAddOn>;
  profileVersions!: Table<ProfileVersion>;
  integrations!: Table<Integration>;
  usageRecords!: Table<any>; // Usage tracking
  karmaProfiles!: Table<KarmaProfile>; // KARMA Profile per instance
  clarityMetrics!: Table<ClarityMetrics>; // Clarity and outcome tracking
  channels!: Table<Channel>; // Team channels
  teamMessages!: Table<TeamMessage>; // Team workspace messages
  apiCalls!: Table<any>; // API call monitoring
  accounts!: Table<Account>; // Ecosystem account (plan, karma, TD1/Karma AC/Realm ids)
  assistantMemory!: Table<AssistantMemoryEntry>; // OpenClaw-style persistent assistant memory

  constructor() {
    super("YourDreamAIDatabase");
    this.version(11).stores({
      profiles: "id, createdAt, updatedAt",
      aiProfiles: "id, shareSlug, customDomain, ownerId, teamId, createdAt, publishedAt",
      conversations: "id, aiId, userId, createdAt, updatedAt, [aiId+userId]",
      vaultFiles: "id, profileId, addedAt",
      vaultChunks: "id, profileId, fileId, createdAt",
      chatMessages: "id, conversationId, profileId, projectId, createdAt",
      toolRegistry: "name",
      projects: "id, profileId, createdAt",
      tiles: "id, projectId, createdAt",
      shareLinks: "id, profileId, slug, createdAt",
      embedConfigs: "profileId",
      analyticsEvents: "id, profileId, eventType, timestamp",
      profileAnalytics: "id, profileId, period",
      teams: "id, ownerId, createdAt",
      teamMembers: "id, teamId, userId",
      comments: "id, profileId, createdAt",
      marketplaceListings: "id, profileId, category, featured, createdAt",
      purchases: "id, listingId, buyerId, purchasedAt",
      subscriptionAddOns: "id, tileId, userId, status, currentPeriodEnd, createdAt",
      profileVersions: "id, profileId, version, createdAt",
      integrations: "id, type, enabled, createdAt",
      usageRecords: "id, userId, profileId, timestamp, action",
      karmaProfiles: "profileId, profileVersion, updatedAt",
      clarityMetrics: "sessionId, profileId, userId, timestamp",
      channels: "id, teamId, createdAt",
      teamMessages: "id, teamId, channelId, senderId, createdAt",
      apiCalls: "id, endpoint, timestamp, status",
    });
    this.version(12).stores({
      accounts: "id, updatedAt",
    });
    this.version(13).stores({
      assistantMemory: "id, userId, aiId, key, [userId+aiId], [userId+aiId+key], updatedAt",
    });
  }
}

// Only initialize database on client side
let dbInstance: YourDreamAIDatabase | null = null;

function getDb(): YourDreamAIDatabase {
  if (typeof window === "undefined") {
    throw new Error("Database can only be accessed on the client side");
  }
  
  if (!dbInstance) {
    dbInstance = new YourDreamAIDatabase();
  }
  
  return dbInstance;
}

// Export getter function for safe access
export { getDb };

// For backward compatibility, export db directly (will be initialized on first access)
// Use a getter function that's called on each property access
function createDbProxy(): YourDreamAIDatabase {
  if (typeof window === "undefined") {
    // Return a minimal proxy for SSR that won't break
    return new Proxy({} as YourDreamAIDatabase, {
      get(target, prop) {
        if (prop === "then" || prop === "catch" || prop === "finally") {
          return undefined;
        }
        // Return a no-op table proxy for SSR
        return new Proxy({}, {
          get() {
            return () => Promise.resolve([]);
          },
        });
      },
    });
  }

  return new Proxy({} as YourDreamAIDatabase, {
    get(target, prop) {
      try {
        const dbInstance = getDb();
        return dbInstance[prop as keyof YourDreamAIDatabase];
      } catch (error) {
        // If database access fails, return a no-op table
        if (prop === "then" || prop === "catch" || prop === "finally") {
          return undefined;
        }
        return new Proxy({}, {
          get() {
            return () => Promise.resolve([]);
          },
        });
      }
    },
    set(target, prop, value) {
      try {
        // Dynamic property access for database operations
        const db = getDb();
        const propKey = prop as keyof YourDreamAIDatabase;
        if (propKey in db) {
          // Use type assertion for dynamic property assignment
          (db as any)[propKey] = value;
        }
        return true;
      } catch (error) {
        return false;
      }
    },
  });
}

export const db = createDbProxy();

